import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Transaction } from '../../../services/types';
import { getCurrencySymbol } from '../../../services/utils';
import type { DeclineContext } from '../../../services/types';
import {
  GateScreen,
  GateButtonStack,
  GatePrimaryButton,
  GateQuietButton,
  LION_GREEN,
  LION_GREEN_LIGHT,
  LION_GREY,
  LION_BORDER,
} from '../components';

const secondsLeft = (expireAt?: string): number | null => {
  if (!expireAt) return null;
  const ms = new Date(expireAt).getTime() - Date.now();
  return ms > 0 ? Math.floor(ms / 1000) : 0;
};
const mmss = (s: number) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const money = (n: number, cur?: string) =>
  `${n.toLocaleString('ru-RU')}${getCurrencySymbol(cur) ?? (cur ? ` ${cur}` : '')}`;

/**
 * Знак валюты рисуется не моноширинным шрифтом экрана: в PT Mono глиф маната (U+20BC) совпадает
 * с рублёвым, и сумма в манатах подписывалась рублём. Начертание легче цифр — знак не перебивает
 * саму сумму.
 */
const CurrencyMark = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
`;

const Money = ({ amount, currency }: { amount: number; currency?: string }) => (
  <>
    {amount.toLocaleString('ru-RU')}
    <CurrencyMark>{getCurrencySymbol(currency) ?? (currency ? ` ${currency}` : '')}</CurrencyMark>
  </>
);

const Title = styled.div`
  font-family: 'PT Mono', monospace;
  font-size: 19px;
  font-weight: 500;
  color: #333;
  margin: 4px 0 8px;
`;

const Hint = styled.div`
  font-family: 'PT Mono', monospace;
  font-size: 13px;
  color: ${LION_GREY};
  line-height: 1.55;
  margin-bottom: 16px;
`;

const Timer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  color: #333;
  background: ${LION_GREEN_LIGHT};
  border: 1px solid ${LION_GREEN};
  border-radius: 999px;
  padding: 6px 16px;
  margin-bottom: 20px;
  & b { color: ${LION_GREEN}; letter-spacing: 1px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const Tile = styled.button<{ $selected: boolean }>`
  padding: 18px 8px;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'PT Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $selected }) => ($selected ? '#fff' : '#333')};
  background: ${({ $selected }) => ($selected ? LION_GREEN : LION_GREEN_LIGHT)};
  border: 1.5px solid ${({ $selected }) => ($selected ? LION_GREEN : LION_BORDER)};
  box-shadow: ${({ $selected }) => ($selected ? '0 6px 16px rgba(106,170,58,0.35)' : 'none')};
  transition: transform 0.12s, box-shadow 0.2s, background 0.15s;
  &:hover:not(:disabled) { transform: translateY(-2px); border-color: ${LION_GREEN}; }
  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { cursor: default; opacity: 0.6; }
`;

// ─── Footer — reuse the requisite-page pattern (two buttons in one row) ───────
interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  /** Cancel as a refusal of the offered amounts. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  onSelectVariant?: (variantId: number) => void;
  variantSubmitting?: boolean;
}

const GateVariantPage = ({ transaction, uuid, timeLeft, onDeclineOffer, onSelectVariant, variantSubmitting }: Props) => {
  const { t } = useTranslation();
  const tx = transaction as any;
  const variants: { id: number; amount: number }[] = tx.variants ?? [];
  const currency = tx.currency ?? tx.requisite?.currency ?? 'RUB';

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [left, setLeft] = useState<number | null>(secondsLeft(tx.variantsExpireAt));
  const [declining, setDeclining] = useState(false);

  // The only way out of this screen besides picking an amount: the ordinary cancel, recorded as a
  // refusal of the offered amounts.
  const declineVariants = async () => {
    if (declining) return;
    setDeclining(true);
    try {
      await onDeclineOffer('variants');
    } finally {
      setDeclining(false);
    }
  };

  useEffect(() => {
    if (!tx.variantsExpireAt) return;
    const id = setInterval(() => setLeft(secondsLeft(tx.variantsExpireAt)), 1000);
    return () => clearInterval(id);
  }, [tx.variantsExpireAt]);

  return (
    <GateScreen
      uuid={uuid}
      timeLeft={timeLeft}
      footerSlot={
        <>
          <GateButtonStack>
            <GatePrimaryButton
              data-sentry-element='onepage-variant-pay'
              disabled={selectedId === null || variantSubmitting}
              onClick={() => selectedId !== null && onSelectVariant?.(selectedId)}
            >
              {t('variant.pay', 'Пополнить')}
            </GatePrimaryButton>
            <GateQuietButton
              data-sentry-element='variant-refuse'
              disabled={declining || variantSubmitting}
              onClick={declineVariants}
            >
              {t('variant.refuse')}
            </GateQuietButton>
          </GateButtonStack>
        </>
      }
    >
      <Title>{t('variant.selectAmount', 'Выберите сумму пополнения')}</Title>
      <Hint>
        {t('variant.intro', {
          amount: money(tx.amount, currency),
          defaultValue: 'Пока не удалось подобрать реквизит под сумму {{amount}}. Ниже есть реквизиты под другие суммы — выберите подходящую, и мы зачислим именно её.',
        })}
      </Hint>

      {left !== null && (
        <Timer>
          {t('variant.offerLeft', 'Осталось выбрать')}: <b>{mmss(left)}</b>
        </Timer>
      )}

      <Grid>
        {variants.map((v) => (
          <Tile
            key={v.id}
            type='button'
            data-sentry-element='onepage-variant-tile'
            $selected={selectedId === v.id}
            disabled={variantSubmitting}
            onClick={() => setSelectedId(v.id)}
          >
            <Money amount={v.amount} currency={currency} />
          </Tile>
        ))}
      </Grid>
    </GateScreen>
  );
};

export default GateVariantPage;
